/**
 * LiteInvitePanel — manager-facing Send Invite affordance for a Lite Profile.
 *
 * Pattern locked 2026-05-06 per /design/V1_Send_Invite_Pattern_Spec.md:
 * the affordance lives INSIDE the Remember callout that already explains
 * why behavioral content is locked. Same callout, revised body copy,
 * inline navy CTA button below the body. The button label dynamically
 * renders the recipient — `Send invite to {work_email}`.
 *
 * 6-state matrix sourced from `/api/profile-invite-status`:
 *
 *   default            → primary "Send invite to {email}" button
 *   sent_cooldown      → disabled "Sent · resend in 24h", timestamp below
 *   resend             → primary "Resend invite" button
 *   declined_cooldown  → updated body copy, disabled "Declined · resend in 24h"
 *   declined_resend    → updated body copy, primary "Resend invite"
 *   no_email           → disabled "Add a work email to send invite", hover-tip
 *
 * Bug history this fixes:
 *   - 2026-05-05 (#38): the Remember callout pointed at a Send Invite
 *     button that didn't exist on the Notes & Tags tab.
 *   - 2026-05-06 (#42): even the v1 LiteInvitePanel I shipped was a separate
 *     panel BELOW the callout, not the inline-in-callout pattern the design
 *     chat locked. This rewrite moves the affordance into the callout,
 *     adds the 6-state matrix, and wires toast confirmations.
 *
 * Backend contract: `/api/consent-send-invite` returns 200 on send,
 * 429 on cooldown (with last_sent_at + can_resend_at + cooldown_remaining_seconds),
 * 502 on Postmark failure. `/api/profile-invite-status` returns the state +
 * timestamps for the 6-state render.
 */

import { useEffect, useState, useCallback } from "react";
import Callout from "./Callout.jsx";
import { useToast } from "./Toast.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";

export default function LiteInvitePanel({ profileId, profile, bearerToken }) {
  const toast = useToast();
  const workEmail = (profile?.work_email || "").trim();
  const displayName = profile?.display_name || "this person";

  const [status, setStatus] = useState(null); // server-derived state envelope
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busy, setBusy] = useState(false);

  // Pull initial state from the audit-events-backed endpoint. Reloads
  // when the bearer token or profile id changes — this includes the
  // post-send refresh below.
  const refreshStatus = useCallback(async () => {
    if (!profileId || !bearerToken) return;
    setLoadingStatus(true);
    try {
      const res = await fetch(
        `/.netlify/functions/profile-invite-status?profile_id=${encodeURIComponent(profileId)}`,
        { headers: { authorization: `Bearer ${bearerToken}` } },
      );
      const body = await parseJsonResponse(res);
      if (res.ok && !body.error) setStatus(body);
      // On error we just leave status null — the panel falls back to the
      // default state, which lets the user attempt the send anyway. The
      // backend will 429 if there's a cooldown they can't see.
    } finally {
      setLoadingStatus(false);
    }
  }, [profileId, bearerToken]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const send = async () => {
    if (!workEmail || busy) return;
    setBusy(true);
    try {
      const res = await fetch("/.netlify/functions/consent-send-invite", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({ profile_id: profileId, recipient_email: workEmail }),
      });
      const body = await parseJsonResponse(res);
      if (res.ok) {
        const days = body.expires_in_days;
        toast.success(
          `Invite sent to ${workEmail}.`,
          typeof days === "number"
            ? `Expires in ${days} days. You can resend after 24 hours.`
            : "You can resend after 24 hours.",
        );
        // Refresh server state so the button transitions to sent_cooldown.
        refreshStatus();
      } else if (res.status === 429) {
        // Cooldown — refresh status so the disabled-button state reflects
        // the server's view (someone else may have sent from another tab).
        toast.error(
          "Couldn't send the invite — already sent within the last 24 hours.",
          "Try again in 24 hours from the original send time.",
        );
        refreshStatus();
      } else {
        toast.error(
          "Couldn't send the invite. The profile is still saved as Lite.",
          body.error || `Try again from the Playbook tab. (HTTP ${res.status})`,
        );
      }
    } catch (e) {
      toast.error(
        "Couldn't send the invite. The profile is still saved as Lite.",
        e.message || "Network error. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Resolve the button state from server-side status + local profile state.
  // The state matrix maps 1:1 to the spec table.
  const buttonState = resolveButtonState({ workEmail, status });

  return (
    <Callout.Remember>
      <CalloutBody state={buttonState} displayName={displayName} />
      <SendInviteButton
        state={buttonState}
        workEmail={workEmail}
        busy={busy || loadingStatus}
        onClick={send}
      />
      {buttonState === "sent_cooldown" && status?.last_sent_at && (
        <LastSentNote iso={status.last_sent_at} />
      )}
      {buttonState === "declined_cooldown" && status?.last_sent_at && (
        <LastSentNote iso={status.last_sent_at} />
      )}
    </Callout.Remember>
  );
}

// ---------------------------------------------------------------------------
// Pure state resolver — exported (named) so tests can pin the matrix.
// ---------------------------------------------------------------------------

export function resolveButtonState({ workEmail, status }) {
  // Trim before the empty check so " " (whitespace-only) gets the
  // no_email treatment too.
  const email = (workEmail || "").trim();
  if (!email) return "no_email";
  // Server may not return a status at all (offline, 5xx) — fall back to default
  // and let the backend cooldown gate handle dupes.
  if (!status) return "default";
  switch (status.state) {
    case "sent_cooldown":     return "sent_cooldown";
    case "resend":            return "resend";
    case "declined_cooldown": return "declined_cooldown";
    case "declined_resend":   return "declined_resend";
    case "redeemed":          return "redeemed"; // unreachable in Lite branch
    case "default":
    default:                  return "default";
  }
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function CalloutBody({ state, displayName }) {
  if (state === "declined_cooldown" || state === "declined_resend") {
    return (
      <p style={bodyStyle}>
        <strong>{displayName}</strong> declined the consent invite. You can
        resend after the 24-hour cooldown — the recipient sees a fresh Decline
        option on every send.
      </p>
    );
  }
  return (
    <p style={bodyStyle}>
      <strong>{displayName}</strong> is a Lite Profile. Behavioral content
      unlocks after consent.
    </p>
  );
}

function SendInviteButton({ state, workEmail, busy, onClick }) {
  const label = buttonLabel(state, workEmail);
  const disabled =
    busy ||
    state === "sent_cooldown" ||
    state === "declined_cooldown" ||
    state === "no_email" ||
    state === "redeemed";
  const isPrimary = state === "default" || state === "resend" || state === "declined_resend";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-state={state}
      data-testid="lite-invite-cta"
      title={hoverTipFor(state)}
      style={{
        marginTop: 12,
        padding: "12px 20px",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "none", // labels include lowercase email; keep mixed-case
        border: isPrimary
          ? "1px solid var(--ppr-color-border-default)"
          : "1px solid var(--ppr-color-border-default)",
        background: isPrimary
          ? "var(--ppr-color-accent-navy)"
          : "var(--ppr-color-background-muted)",
        color: isPrimary
          ? "var(--ppr-color-text-inverse)"
          : "var(--ppr-color-text-muted)",
        cursor: disabled ? "not-allowed" : (busy ? "wait" : "pointer"),
        opacity: disabled ? 0.85 : 1,
        borderRadius: 6,
        fontWeight: 600,
      }}
    >
      {busy ? "Sending…" : label}
    </button>
  );
}

function LastSentNote({ iso }) {
  let formatted = iso;
  try {
    const d = new Date(iso);
    // mono-register UTC timestamp per spec example
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mn = String(d.getUTCMinutes()).padStart(2, "0");
    formatted = `${yyyy}-${mm}-${dd} ${hh}:${mn} UTC`;
  } catch { /* fall through to raw iso */ }
  return (
    <p
      style={{
        marginTop: 8,
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: 11,
        color: "var(--ppr-color-text-muted)",
      }}
    >
      last sent {formatted}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function buttonLabel(state, workEmail) {
  switch (state) {
    case "default":            return `Send invite to ${truncateEmail(workEmail)}`;
    case "sent_cooldown":      return "Sent · resend in 24h";
    case "resend":             return "Resend invite";
    case "declined_cooldown":  return "Declined · resend in 24h";
    case "declined_resend":    return "Resend invite";
    case "no_email":           return "Add a work email to send invite";
    case "redeemed":           return "Consent confirmed";
    default:                   return "Send invite";
  }
}

/**
 * Truncate an email longer than 32 characters at the @-boundary, replacing
 * the local-part midsection with an ellipsis. Per spec.
 */
export function truncateEmail(email, max = 32) {
  if (!email || email.length <= max) return email;
  const at = email.indexOf("@");
  if (at < 0) return email.slice(0, max - 1) + "…";
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  // Keep enough domain characters to be recognizable: up to 12 chars
  // including the @ + ellipsis.
  const localBudget = Math.max(4, max - domain.length - 2); // -2 for "@…"
  if (localBudget < local.length) {
    return `${local.slice(0, localBudget)}…@${domain}`;
  }
  return email;
}

function hoverTipFor(state) {
  switch (state) {
    case "sent_cooldown":
      return "You can resend after 24 hours. The recipient still has the original link — it's valid for 7 days.";
    case "declined_cooldown":
      return "Recipient declined. You can resend after the 24-hour cooldown elapses.";
    case "no_email":
      return "Edit the profile to add a work email.";
    default:
      return "";
  }
}

const bodyStyle = {
  margin: 0,
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 15,
  lineHeight: 1.55,
  color: "var(--ppr-color-text-primary)",
};
